const PORTAL_ORIGINS = new Set([
  "https://portal.systemlabs.com",
  "https://portal-staging.systemlabs.com",
]);

export type MessageLink = {
  kind: "link" | "guide" | "learning";
  href: string;
  label: string;
  token?: string;
  slug?: string;
  resourceKind?: "guide" | "article";
};
export type MessagePart = MessageLink | { kind: "text"; value: string };

export function safeMessageUrl(value: string): string | null {
  const raw = value.trim();
  if (/^\/(?:app(?:[/?#]|$)|guides\/)/.test(raw) && !raw.includes("\\"))
    return raw;
  const absolute = /^(?:www\.|portal(?:-staging)?\.systemlabs\.com\/)/i.test(
    raw,
  )
    ? `https://${raw}`
    : raw;
  try {
    const url = new URL(absolute);
    return ["https:", "http:", "mailto:"].includes(url.protocol) &&
      !url.username &&
      !url.password
      ? absolute
      : null;
  } catch {
    return null;
  }
}

export function describeMessageLink(href: string, label = href): MessageLink {
  const result: MessageLink = { kind: "link", href, label };
  const url = new URL(href, "https://portal.systemlabs.com");
  const local =
    href.startsWith("/") ||
    PORTAL_ORIGINS.has(url.origin) ||
    (typeof window !== "undefined" && url.origin === window.location.origin);
  if (!local || url.username || url.password) return result;
  const pdf = url.pathname.match(/^\/guides\/([A-Za-z0-9_-]+)\/?$/);
  if (pdf) return { ...result, kind: "guide", token: pdf[1] };
  const learn = url.pathname.match(
    /^\/app\/learn\/(article\/)?([A-Za-z0-9_-]+)\/?$/,
  );
  return learn
    ? {
        ...result,
        kind: "learning",
        slug: learn[2],
        resourceKind: learn[1] ? "article" : "guide",
      }
    : result;
}

// Code and Markdown links are consumed whole before matching bare URLs. This
// avoids splitting Markdown around a preview URL or linkifying code examples.
const TOKENS =
  /```[\s\S]*?```|`[^`\n]*`|!?\[([^\]\n]*)\]\(<?((?:[^\s()<>]|\([^\s()]*\))+)>?(?:\s+"[^"]*")?\)|<?(?:https?:\/\/|www\.|portal(?:-staging)?\.systemlabs\.com\/|\/(?:app|guides)(?=[/?#]))[^\s<>"`]+>?/gi;

function trimPunctuation(value: string) {
  let url = value.replace(/[.,!?;:*]+$/, "");
  while (/[)\]]$/.test(url)) {
    const close = url.slice(-1),
      open = close === ")" ? "(" : "[";
    if (url.split(close).length <= url.split(open).length) break;
    url = url.slice(0, -1);
  }
  return url;
}

export function splitMessageContent(
  text: string,
  { literalImages = false }: { literalImages?: boolean } = {},
): MessagePart[] {
  const parts: MessagePart[] = [];
  let cursor = 0;
  for (const match of text.matchAll(TOKENS)) {
    const raw = match[0],
      start = match.index!;
    if (raw.startsWith("`")) continue;
    // Skip embedded paths such as the /app part of an unrecognized hostname.
    if (raw.startsWith("/") && start > 0 && /[\w/]/.test(text[start - 1]))
      continue;
    const marked = match[2] !== undefined;
    const angled = raw.startsWith("<") && raw.endsWith(">");
    const value = marked
      ? match[2]
      : trimPunctuation(angled ? raw.slice(1, -1) : raw);
    const href = safeMessageUrl(value);
    if (!href) continue;
    // Plain text shown as typed (a rep's message) keeps image syntax as text,
    // and only its URL becomes a link (system-support#689).
    if (literalImages && marked && raw.startsWith("!")) {
      const urlAt = raw.indexOf("](") + 2;
      const urlStart = start + urlAt + (raw[urlAt] === "<" ? 1 : 0);
      parts.push({ kind: "text", value: text.slice(cursor, urlStart) });
      parts.push(describeMessageLink(href, value));
      cursor = urlStart + value.length;
      continue;
    }
    const end = marked || angled ? start + raw.length : start + value.length;
    if (start > cursor)
      parts.push({ kind: "text", value: text.slice(cursor, start) });
    parts.push(describeMessageLink(href, marked ? match[1] : value));
    cursor = end;
  }
  if (cursor < text.length)
    parts.push({ kind: "text", value: text.slice(cursor) });
  return parts.length ? parts : [{ kind: "text", value: text }];
}

export function linkifyMessageMarkdown(parts: MessagePart[]): string {
  return parts
    .map((part) =>
      part.kind === "text"
        ? part.value
        : `[${part.label.replace(/[\\[\]]/g, "\\$&")}](<${part.href}>)`,
    )
    .join("");
}
